import { type ReactElement, useState, type MouseEvent } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu, { type MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';

import { kebabCase } from '../../utils/stringHelpers';

export type DropdownOption<T> = {
  label: string;
  value: T;
  disabled?: boolean;
};

export type DropdownButtonMenuProps = Omit<MenuProps, 'open' | 'anchorEl' | 'children'>;

export type DropdownButtonProps<T> = {
  label: string;
  dropdownOptions: Array<DropdownOption<T>>;
  variant?: 'text' | 'outlined' | 'contained';
  onSelect: (value: T) => void;
  disabled?: boolean;
  dataTestID?: string;
  /** Extra props forwarded to MUI Menu (open/anchorEl/children are controlled). */
  MenuProps?: DropdownButtonMenuProps;
};

/**
 * Outlined dropdown button with menu options (One-Offs Actions pattern).
 * @see https://mui.com/material-ui/react-menu/#customization
 */
export function DropdownButton<T>({
  label,
  dropdownOptions,
  variant = 'outlined',
  onSelect,
  disabled,
  dataTestID = 'dropdown-button',
  MenuProps,
}: Readonly<DropdownButtonProps<T>>): ReactElement {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const { onClose: menuOnClose, ...restMenuProps } = MenuProps ?? {};

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (value: T) => {
    setAnchorEl(null);
    onSelect(value);
  };

  return (
    <Box>
      <Button
        data-testid={dataTestID}
        variant={variant}
        onClick={handleClick}
        endIcon={<KeyboardArrowDown />}
        disabled={disabled}
      >
        {label}
      </Button>
      <Menu
        data-testid="dropdown-menu"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        {...restMenuProps}
        open={open}
        anchorEl={anchorEl}
        onClose={(event, reason) => {
          menuOnClose?.(event, reason);
          handleClose();
        }}
      >
        {dropdownOptions.map((option) => (
          <MenuItem
            key={String(option.value)}
            data-testid={`${kebabCase(String(option.value))}-option`}
            onClick={() => handleAction(option.value)}
            disabled={option.disabled}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

export default DropdownButton;
